import { lazy, Suspense } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

// ─── Desktop versions (the existing pages, moved to desktop/) ───────────────
const DesktopIndex = lazy(() => import("@/pages/desktop/Index"));
const DesktopDiscover = lazy(() => import("@/pages/desktop/Discover"));
const DesktopMatches = lazy(() => import("@/pages/desktop/Matches"));
const DesktopMessages = lazy(() => import("@/pages/desktop/Messages"));
const DesktopDashboard = lazy(() => import("@/pages/desktop/Dashboard"));

// ─── Mobile versions (brand-new app-like pages) ────────────────────────────
const MobileIndex = lazy(() => import("@/pages/mobile/Index"));
const MobileDiscover = lazy(() => import("@/pages/mobile/Discover"));
const MobileMatches = lazy(() => import("@/pages/mobile/Matches"));
const MobileMessages = lazy(() => import("@/pages/mobile/Messages"));
const MobileDashboard = lazy(() => import("@/pages/mobile/Dashboard"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// Helper: renders the correct version based on device
function DevicePage({
  Mobile,
  Desktop,
}: {
  Mobile: React.ComponentType;
  Desktop: React.ComponentType;
}) {
  const isMobile = useIsMobile();
  // undefined means we haven't measured yet — show nothing to avoid flash
  if (isMobile === undefined) return <PageLoader />;
  return (
    <Suspense fallback={<PageLoader />}>
      {isMobile ? <Mobile /> : <Desktop />}
    </Suspense>
  );
}

// ─── Exported route components ─────────────────────────────────────────────
export const IndexPage = () => (
  <DevicePage Mobile={MobileIndex} Desktop={DesktopIndex} />
);

export const DiscoverPage = () => (
  <DevicePage Mobile={MobileDiscover} Desktop={DesktopDiscover} />
);

export const MatchesPage = () => (
  <DevicePage Mobile={MobileMatches} Desktop={DesktopMatches} />
);

export const MessagesPage = () => (
  <DevicePage Mobile={MobileMessages} Desktop={DesktopMessages} />
);

export const DashboardPage = () => (
  <DevicePage Mobile={MobileDashboard} Desktop={DesktopDashboard} />
);
